import React, { useState, useContext, useEffect } from 'react';
import moment from 'moment-timezone';
import { useIntl } from 'react-intl';
import { DATE_PICKER_SUGGEST_BOX_WIDTH_PX } from '@mummssoftware/common-ui';
import { emptyNoteText, PSO, CPF, error } from '@mummssoftware/utils/constants';
import { isAgencyEnabled } from '@mummssoftware/utils/utils';
import { GlobalContext } from '@mummssoftware/global-context/GlobalContext';
import { formatLocDateMMDDYYYY } from '@mummssoftware/utils/formatters';
import { VFGlobalContext } from '@mummssoftware/global-context';
import { ADD_ORDER_DATE } from '@mummssoftware/global-context/VFGlobalContext';
import {
  getMinVerbalDate,
  getUserCredentials,
  getLocalOrderTime,
} from '@mummssoftware/utils/orders/orders-utils';
import {
  OTH,
  MMD,
  ADM,
  IA,
  VA,
  PPN,
} from '@mummssoftware/utils/orders/orders-constants';
import {
  conversationTypes,
  orderTypeMapping,
} from '@mummssoftware/utils/orders';
import {
  OrdersGlobalContext,
  SET_CONVO_TYPE,
  SET_ORDER_PHYSICIAN,
  SET_VERBAL_ORDER_DATE,
  SET_CREATE_ORDER,
  SET_ORDER_STATEMENT,
} from '@mummssoftware/global-context/OrdersGlobalContext';
import {
  PhysicianPicker,
  VerbalPicker,
  ConversationPicker,
} from '../Components/components';
import {
  SET_ORDER_DISABLED,
  SET_APPROVAL_DATE_ERROR,
  SET_PECOS_OPEN,
} from '../Context/constants';
import { ProgressNotesContext } from '../Context/ProgressNotesContext';
import { Text } from '../../components/layout/WrapperDiv';
import { Context } from '../../context';
import defaults from '../../translations/en.json';
import { isDisType } from './utils';

type ApprovalStatementProps = {
  intl: any;
};

const ApprovalStatement = () => {
  const { patient, currentUser, savedApproval } = useContext(Context);

  const {
    orderDisabled,
    dispatch,
    orderCreated,
    disOrderMap,
    admitPhysicianList,
    isOverrideVerifOrderAgency,
    checkIfPECOSEligible,
    pickerDialogHeight,
    handleGenericOrder,
    handleSendApproval,
  } = useContext(ProgressNotesContext);
  const {
    orderType,
    savedNote,
    convoType,
    verbalDate,
    admitPhysician,
    createOrder,
    orderStatement,
    dispatchOrders,
  } = useContext(OrdersGlobalContext);

  const intl = useIntl();

  const { dispatchVF } = useContext(VFGlobalContext);
  const { doctorsGlobal, patientGlobal, patientDateRestrictions } = useContext(
    GlobalContext,
  );

  const isDISOrder =
    orderType && isDisType(orderType) && disOrderMap && disOrderMap[orderType];

  // verbal date should not have a default value
  const userTitle = getUserCredentials(currentUser);

  // referralDate is the MIN date for VERBAL DATE for LOC and LC
  const referralDate =
    patientGlobal && moment((patientGlobal as mumms.patient).referralDate);

  // on Load set default verbal date and physician
  useEffect(() => {
    if (orderType !== CPF) {
      dispatchOrders({
        type: SET_CONVO_TYPE,
        payload: conversationTypes[0],
      });
    }

    // set to true at mount time
    dispatch({
      type: SET_APPROVAL_DATE_ERROR,
      payload: true,
    });
    // create order button should not be disabled for PSO workflow
    if (orderType === PSO && orderDisabled) {
      dispatch({
        type: SET_ORDER_DISABLED,
        payload: false,
      });
    }
  }, []);

  const verbalDateMin: any = getMinVerbalDate(
    referralDate,
    patientDateRestrictions,
    orderType,
  );

  const [errorDate, setErrorDate] = useState<boolean>(false);

  const handleDateChange = (approvaldate: Date | null) => {
    // todo modify not needed CPF check
    if (orderType === CPF) {
      dispatchVF({
        type: ADD_ORDER_DATE,
        payload: approvaldate,
      });
    }
    const newDate = approvaldate || new Date();
    const newMoment = moment(newDate);
    dispatchOrders({
      type: SET_VERBAL_ORDER_DATE,
      payload: newDate,
    });
    // minimumDate={verbalDateMin}
    // maximumDate={Date.now()}
    const isInvalidDate = !newMoment.isValid();
    const maxDateError = newMoment.diff(moment()) > 0;
    const minDateError = newMoment.diff(moment(verbalDateMin)) < 0;
    if (maxDateError || minDateError || isInvalidDate) {
      setErrorDate(true);
      dispatch({
        type: SET_APPROVAL_DATE_ERROR,
        payload: true,
      });
    } else {
      setErrorDate(false);
      dispatch({
        type: SET_APPROVAL_DATE_ERROR,
        payload: false,
      });
    }
  };

  const checkAdmitOrder = async () => {
    const ignorePECOS = isAgencyEnabled(
      window.env.ignorePECOSRequirement,
      currentUser,
    );
    if (ignorePECOS) {
      // then proceed
      getFirstWorkflow();
    } else if (verbalDate && admitPhysician) {
      const checkStatus = await checkIfPECOSEligible();
      // if PECOS_ELIGIBLE
      if (checkStatus.admitOk) {
        // then proceed
        getFirstWorkflow();
      } else {
        // else reset and open dialog
        dispatchOrders({
          type: SET_CREATE_ORDER,
          payload: false,
        });
        dispatch({
          type: SET_PECOS_OPEN,
          payload: true,
        });
      }
    }
  };

  const handlePhysicianChange = (physician?: any) => {
    dispatchOrders({
      type: SET_ORDER_PHYSICIAN,
      payload: physician,
    });
  };

  const handleConversationChange = (conversation?: any) => {
    dispatchOrders({
      type: SET_CONVO_TYPE,
      payload: conversation,
    });
  };

  // getTextContent for the approval text
  const getTextContent = () => {
    const approvalPayload = intl.formatMessage(defaults.approvalOrder, {
      userTitle,
      conversation: (convoType as mumms.OptionType)?.label,
      date: formatLocDateMMDDYYYY(verbalDate),
      // OP-33278: pick-list shows "Last, First"; the order sentence keeps "First Last"
      doctor: admitPhysician
        ? admitPhysician?.fullName || admitPhysician?.label
        : '',
    });
    dispatchOrders({
      type: SET_ORDER_STATEMENT,
      payload: approvalPayload,
    });
  };

  const getFirstWorkflow = () => {
    getTextContent();
    if (orderType === PSO) {
      // gather the other statement
      setTimeout(() => {
        const payload = intl.formatMessage(defaults.psoOrder, {
          userCredentials: userTitle,
          signDate: getLocalOrderTime(currentUser),
        });
        dispatchOrders({
          type: SET_ORDER_STATEMENT,
          payload,
        });
      }, 100);
    }

    // First API call is send approval for all order type
    // if the call is successful (savedApproval) the next chain of API calls is triggered
    handleSendApproval();
  };
  useEffect(() => {
    if (createOrder) {
      // if ADMIT and isOverrideVerifOrderAgency then checkIfPECOSEligible here
      if (orderType === ADM && isOverrideVerifOrderAgency) {
        checkAdmitOrder();
      } else {
        getFirstWorkflow();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createOrder]);

  useEffect(() => {
    // if approval API successful then next API call
    if (
      createOrder &&
      !orderCreated &&
      savedApproval &&
      (orderType === OTH || orderType === PSO || orderType === MMD)
    ) {
      // setTimeout to make sure we get the correct orderStatement
      setTimeout(() => {
        handleGenericOrder();
      }, 400);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createOrder, savedApproval]);

  const approvalError = !admitPhysician || errorDate || !verbalDate;
  // set the order status for the submit button
  useEffect(() => {
    if (approvalError) {
      dispatch({
        type: SET_ORDER_DISABLED,
        payload: true,
      });
    } else {
      dispatch({
        type: SET_ORDER_DISABLED,
        payload: false,
      });
    }
  }, [approvalError]);

  const isAdmitType = orderType === ADM;
  const doctorList = isAdmitType ? admitPhysicianList : doctorsGlobal;

  return (
    <>
      <Text bold>{userTitle}</Text>
      <Text>{defaults.textOrder.have}</Text>
      <ConversationPicker
        options={conversationTypes}
        handleConversationChange={handleConversationChange}
        value={convoType}
      />
      <Text>{defaults.textOrder.communication}</Text>
      <VerbalPicker
        selectedDate={verbalDate}
        handleVerbalDateChange={handleDateChange}
        minimumDate={verbalDateMin}
        maximumDate={Date.now()}
        width={DATE_PICKER_SUGGEST_BOX_WIDTH_PX}
        severity={!verbalDate || errorDate ? error : null}
      />
      <Text>{defaults.textOrder.with}</Text>
      <PhysicianPicker
        doctor={admitPhysician as mumms.Doctor}
        doctorsOptions={(doctorList as mumms.Doctor[]) || []}
        handlePhysicianChange={handlePhysicianChange}
        severity={admitPhysician ? null : error}
        maxMenuHeight={pickerDialogHeight}
        // menuIsOpen
      />
      <Text>{defaults.textOrder.wherein}</Text>
      <Text bold>
        {admitPhysician
          ? (admitPhysician as any)?.fullName || (admitPhysician as any)?.label
          : defaults.physician}
      </Text>
    </>
  );
};

export default ApprovalStatement;
